import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import './ClassManagement.css';

const ClassManagement = ({ embedded = false }) => {
  const { user } = useAuth();
  const toast = useToast();

  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [roster, setRoster] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [classSchedule, setClassSchedule] = useState([]);
  const [showAddSchedule, setShowAddSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    day_of_week: 'Monday',
    start_time: '17:00',
    end_time: '19:00',
    start_date: '',   // optional season start (blank = open-ended)
    end_date: ''      // optional season end
  });
  const [editingScheduleId, setEditingScheduleId] = useState(null); // null = adding; id = editing that practice

  const BLANK_CLASS = {
    class_name: '',
    level: '',
    program_type: 'competitive',
    age_range: '',
    max_capacity: '',
    monthly_tuition: '',
    registration_fee: '',
    description: '',
    pricing_options: []
  };
  const [formData, setFormData] = useState(BLANK_CLASS);
  const [editingClassId, setEditingClassId] = useState(null); // null = creating; id = editing that class

  const [enrollData, setEnrollData] = useState({
    athlete_id: '',
    tuition_amount: '',
    payment_frequency: 'monthly',
    billing_option: '',
    discount_type: 'none',
    discount_value: '',
    discount_reason: ''
  });

  // Per-athlete billing edit (custom tuition / sibling discount) from the roster
  const [editEnrollment, setEditEnrollment] = useState(null);

  // Parse a class's admin-defined pricing options (stored as JSON)
  const parseOptions = (cls) => {
    if (!cls) return [];
    try {
      const p = typeof cls.pricing_options === 'string' ? JSON.parse(cls.pricing_options) : cls.pricing_options;
      return Array.isArray(p) ? p : [];
    } catch { return []; }
  };
  // Net monthly after an enrollment's recurring discount
  const netOf = (tuition, dtype, dval) => {
    const base = Number(tuition) || 0;
    const v = Number(dval) || 0;
    let net = base;
    if (dtype === 'percent') net = base - (base * v) / 100;
    else if (dtype === 'fixed') net = base - v;
    return Math.max(0, Math.round(net * 100) / 100);
  };

  // Pricing-options editor (class create modal)
  const addOpt = () => setFormData(f => ({ ...f, pricing_options: [...f.pricing_options, { label: '', amount: '', cadence: 'monthly' }] }));
  const updateOpt = (i, k, v) => setFormData(f => { const o = [...f.pricing_options]; o[i] = { ...o[i], [k]: v }; return { ...f, pricing_options: o }; });
  const removeOpt = (i) => setFormData(f => ({ ...f, pricing_options: f.pricing_options.filter((_, idx) => idx !== i) }));
  // Multi-enroll: athletes checked in the enroll modal (enrolled in one action).
  const [selectedAthleteIds, setSelectedAthleteIds] = useState([]);

  // Only admins may change amounts billed to an athlete (tuition / discounts).
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchClasses();
    fetchAthletes();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchClassDetails();
    }
  }, [selectedClass, activeTab]);

  const handleSelectClass = (cls) => {
    setSelectedClass(cls);
    setActiveTab('roster'); // Default to roster tab when selecting a class
  };

  const fetchClasses = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('user') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/classes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setClasses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setClasses([]);
    }
  };

  const fetchAthletes = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('user') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/athletes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAthletes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching athletes:', error);
      setAthletes([]);
    }
  };

  const fetchClassDetails = async () => {
    try {
      console.log('[fetchClassDetails] activeTab:', activeTab, 'selectedClass:', selectedClass?.id);
      
      if (activeTab === 'roster') {
        console.log('[fetchClassDetails] Fetching roster...');
        const response = await fetch(`${API_BASE_URL}/api/classes/${selectedClass.id}/roster`, {
          headers: { 'Authorization': `Bearer ${user?.token}` }
        });
        
        const data = await response.json();
        console.log('[fetchClassDetails] Roster data:', data);
        setRoster(Array.isArray(data) ? data : []);
      } else if (activeTab === 'waitlist') {
        const response = await fetch(`${API_BASE_URL}/api/classes/${selectedClass.id}/waitlist`, {
          headers: { 'Authorization': `Bearer ${user?.token}` }
        });
        const data = await response.json();
        setWaitlist(Array.isArray(data) ? data : []);
      } else if (activeTab === 'schedule') {
        fetchSchedule();
      }
    } catch (error) {
      console.error('Error fetching class details:', error);
    }
  };

  const fetchSchedule = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/classes/${selectedClass.id}/schedule`, {
        headers: { 'Authorization': `Bearer ${user?.token}` }
      });
      const data = await response.json();
      setClassSchedule(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching schedule:', error);
      setClassSchedule([]);
    }
  };

  const handleAddSchedule = async (e) => {
    e.preventDefault();
    try {
      const editing = editingScheduleId != null;
      const url = editing
        ? `${API_BASE_URL}/api/classes/${selectedClass.id}/schedule/${editingScheduleId}`
        : `${API_BASE_URL}/api/classes/${selectedClass.id}/schedule`;
      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify(scheduleForm)
      });

      if (response.ok) {
        toast.success(editing ? 'Practice time updated!' : 'Practice time added successfully!');
        setShowAddSchedule(false);
        setEditingScheduleId(null);
        setScheduleForm({ day_of_week: 'Monday', start_time: '17:00', end_time: '19:00', start_date: '', end_date: '' });
        fetchSchedule();
      } else {
        toast.error(editing ? 'Failed to update practice time' : 'Failed to add practice time');
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
    }
  };

  // Prefill the schedule modal to edit an existing practice in place.
  const openEditSchedule = (schedule) => {
    setEditingScheduleId(schedule.id);
    setScheduleForm({
      day_of_week: schedule.day_of_week,
      start_time: String(schedule.start_time || '').slice(0, 5),
      end_time: String(schedule.end_time || '').slice(0, 5),
      start_date: schedule.start_date ? String(schedule.start_date).slice(0, 10) : '',
      end_date: schedule.end_date ? String(schedule.end_date).slice(0, 10) : '',
    });
    setShowAddSchedule(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Remove this practice time?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/classes/${selectedClass.id}/schedule/${scheduleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${user?.token}` }
      });

      if (response.ok) {
        toast.success('Practice time removed');
        fetchSchedule();
      } else {
        toast.error('Failed to remove practice time');
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    const editing = editingClassId != null;
    try {
      const token = JSON.parse(localStorage.getItem('user') || '{}').token;
      const response = await fetch(
        `${API_BASE_URL}/api/classes${editing ? `/${editingClassId}` : ''}`, {
          method: editing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

      if (response.ok) {
        toast.success(editing ? 'Class updated!' : 'Class created successfully!');
        setShowCreateModal(false);
        // Keep the open detail panel in sync with the edit.
        if (editing) setSelectedClass(prev => (prev && prev.id === editingClassId ? { ...prev, ...formData } : prev));
        setEditingClassId(null);
        setFormData(BLANK_CLASS);
        fetchClasses();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.error || (editing ? 'Failed to update class' : 'Failed to create class'));
      }
    } catch (error) {
      console.error('Error saving class:', error);
      toast.error(editing ? 'Error updating class' : 'Error creating class');
    }
  };

  // Open the create modal prefilled to EDIT an existing class (name, price, etc.).
  const openEditClass = (cls) => {
    let pricing = [];
    try { pricing = cls.pricing_options ? (typeof cls.pricing_options === 'string' ? JSON.parse(cls.pricing_options) : cls.pricing_options) : []; } catch { pricing = []; }
    setFormData({
      class_name: cls.class_name || '',
      level: cls.level || '',
      program_type: cls.program_type || 'competitive',
      age_range: cls.age_range || '',
      max_capacity: cls.max_capacity ?? '',
      monthly_tuition: cls.monthly_tuition ?? '',
      registration_fee: cls.registration_fee ?? '',
      description: cls.description || '',
      pricing_options: Array.isArray(pricing) ? pricing : []
    });
    setEditingClassId(cls.id);
    setShowCreateModal(true);
  };

  const openCreateClass = () => {
    setEditingClassId(null);
    setFormData(BLANK_CLASS);
    setShowCreateModal(true);
  };

  const handleDeleteClass = async (cls) => {
    const enrolled = cls.current_enrollment || 0;
    if (!window.confirm(`Delete "${cls.class_name}"?${enrolled ? ` It has ${enrolled} enrolled athlete(s).` : ''}\n\nThis cannot be undone.`)) return;
    try {
      const token = JSON.parse(localStorage.getItem('user') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/classes/${cls.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Class deleted.');
        if (selectedClass?.id === cls.id) setSelectedClass(null);
        fetchClasses();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.error || 'Failed to delete class');
      }
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error('Error deleting class');
    }
  };

  const handleEnrollAthlete = async (e) => {
    e.preventDefault();
    if (selectedAthleteIds.length === 0) {
      toast.error('Select at least one athlete to enroll.');
      return;
    }
    try {
      // Enroll each selected athlete (reuses the single-enroll endpoint); the
      // shared tuition/frequency below applies to everyone in this batch.
      const results = await Promise.allSettled(
        selectedAthleteIds.map((id) =>
          fetch(`${API_BASE_URL}/api/classes/${selectedClass.id}/enroll`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${user?.token}`
            },
            body: JSON.stringify({
              athlete_id: id,
              tuition_amount: enrollData.tuition_amount || null,
              payment_frequency: enrollData.payment_frequency || 'monthly',
              billing_option: enrollData.billing_option || null,
              discount_type: enrollData.discount_type || 'none',
              discount_value: enrollData.discount_value || 0,
              discount_reason: enrollData.discount_reason || null,
              enrollment_date: new Date().toISOString().split('T')[0]
            })
          }).then(async (r) => {
            if (!r.ok) {
              const j = await r.json().catch(() => ({}));
              throw new Error(j.error || 'enroll failed');
            }
            return r;
          })
        )
      );

      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      if (ok > 0) {
        toast.success(`Enrolled ${ok} athlete${ok > 1 ? 's' : ''}` + (failed ? ` — ${failed} failed` : ''));
      } else {
        toast.error('Failed to enroll the selected athletes.');
      }

      // Refresh roster once after the batch.
      const rosterResponse = await fetch(`${API_BASE_URL}/api/classes/${selectedClass.id}/roster`, {
        headers: { 'Authorization': `Bearer ${user?.token}` }
      });
      const rosterData = await rosterResponse.json();
      setRoster(Array.isArray(rosterData) ? rosterData : []);
      fetchClassDetails();

      setSelectedAthleteIds([]);
      setEnrollData({ athlete_id: '', tuition_amount: '', payment_frequency: 'monthly', billing_option: '', discount_type: 'none', discount_value: '', discount_reason: '' });
      if (ok > 0) setShowEnrollModal(false);
    } catch (error) {
      console.error('Error enrolling athletes:', error);
      toast.error('Error enrolling athletes: ' + error.message);
    }
  };

  // Save a per-athlete billing edit (custom tuition / sibling discount)
  const handleSaveEnrollmentEdit = async () => {
    if (!editEnrollment) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/classes/${selectedClass.id}/enrollments/${editEnrollment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user?.token}` },
        body: JSON.stringify({
          tuition_amount: editEnrollment.tuition_amount === '' ? null : editEnrollment.tuition_amount,
          discount_type: editEnrollment.discount_type || 'none',
          discount_value: editEnrollment.discount_value || 0,
          discount_reason: editEnrollment.discount_reason || null
        })
      });
      if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'update failed');
      toast.success(`Updated billing for ${editEnrollment.athlete_name}`);
      setEditEnrollment(null);
      const rosterResponse = await fetch(`${API_BASE_URL}/api/classes/${selectedClass.id}/roster`, {
        headers: { 'Authorization': `Bearer ${user?.token}` }
      });
      setRoster(await rosterResponse.json());
    } catch (error) {
      console.error('Error updating enrollment:', error);
      toast.error('Could not update: ' + error.message);
    }
  };

  const handlePromoteFromWaitlist = async (item) => {
    if (!window.confirm(`Promote ${item.athlete_name} from the waitlist to active enrollment?`)) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/classes/${selectedClass.id}/waitlist/${item.id}/promote`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${user?.token}` }
      });
      const result = await response.json();
      if (response.ok) {
        toast.success(`${item.athlete_name} has been promoted to active enrollment!`);
        fetchClassDetails();
      } else {
        toast.error(result.error || 'Failed to promote athlete');
      }
    } catch (error) {
      console.error('Error promoting from waitlist:', error);
      toast.error('Error promoting athlete from waitlist');
    }
  };

  const handleDropAthlete = async (enrollmentId) => {
    if (!window.confirm('Are you sure you want to drop this athlete from the class?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/classes/${selectedClass.id}/enrollments/${enrollmentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify({
          status: 'dropped',
          end_date: new Date().toISOString().split('T')[0]
        })
      });

      if (response.ok) {
        toast.success('Athlete dropped from class');
        fetchClassDetails();
      } else {
        toast.error('Failed to drop athlete');
      }
    } catch (error) {
      console.error('Error dropping athlete:', error);
    }
  };

  return (
    <div className="class-management">
      <div className="header">
        {!embedded && <h1>Class Management</h1>}
        <button className="btn-primary" onClick={openCreateClass}>
          + Create New Class
        </button>
      </div>

      <div className="content-layout">
        {/* Class List Sidebar */}
        <div className="class-list">
          <h2>Classes</h2>
          {classes.map((cls) => (
            <div
              key={cls.id}
              className={`class-card ${selectedClass?.id === cls.id ? 'selected' : ''}`}
              onClick={() => handleSelectClass(cls)}
            >
              <div className="class-header">
                <h3>{cls.class_name}</h3>
                <span className={`badge ${cls.is_active ? 'active' : 'inactive'}`}>
                  {cls.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="level">{cls.level} • {cls.program_type}</p>
              <div className="capacity">
                <span>{cls.current_enrollment || 0} / {cls.max_capacity || '∞'} enrolled</span>
              </div>
              {cls.monthly_tuition && (
                <p className="tuition">${cls.monthly_tuition}/month</p>
              )}
            </div>
          ))}
        </div>

        {/* Class Details Panel */}
        <div className="class-details">
          {selectedClass ? (
            <>
              <div className="details-header">
                <div>
                  <h2>{selectedClass.class_name}</h2>
                  <p>{selectedClass.level} • {selectedClass.program_type}</p>
                </div>
                <div className="actions">
                  <button className="btn-secondary" onClick={() => {
                    setSelectedAthleteIds([]);
                    // Pre-fill tuition from the class's monthly cost so billing "just works"
                    setEnrollData(d => ({ ...d, tuition_amount: selectedClass.monthly_tuition || '' }));
                    setShowEnrollModal(true);
                  }}>
                    Enroll Athletes
                  </button>
                  <button className="btn-secondary" onClick={() => openEditClass(selectedClass)}>
                    ✏️ Edit Class
                  </button>
                  {isAdmin && (
                    <button className="btn-secondary" style={{ color: '#b91c1c', borderColor: '#fecaca' }}
                      onClick={() => handleDeleteClass(selectedClass)}>
                      🗑 Delete
                    </button>
                  )}
                </div>
              </div>

              <div className="tabs">
                <button
                  className={activeTab === 'overview' ? 'active' : ''}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </button>
                <button
                  className={activeTab === 'roster' ? 'active' : ''}
                  onClick={() => setActiveTab('roster')}
                >
                  Roster ({roster.length})
                </button>
                <button
                  className={activeTab === 'schedule' ? 'active' : ''}
                  onClick={() => setActiveTab('schedule')}
                >
                  Schedule
                </button>
                <button
                  className={activeTab === 'waitlist' ? 'active' : ''}
                  onClick={() => setActiveTab('waitlist')}
                >
                  Waitlist ({waitlist.length})
                </button>
              </div>

              <div className="tab-content">
                {activeTab === 'overview' && (
                  <div className="overview">
                    <div className="info-grid">
                      <div className="info-item">
                        <label>Age Range</label>
                        <p>{selectedClass.age_range || 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Capacity</label>
                        <p>{selectedClass.current_enrollment || 0} / {selectedClass.max_capacity || '∞'}</p>
                      </div>
                      <div className="info-item">
                        <label>Monthly Tuition</label>
                        <p>${selectedClass.monthly_tuition || 0}</p>
                      </div>
                      <div className="info-item">
                        <label>Registration Fee</label>
                        <p>${selectedClass.registration_fee || 0}</p>
                      </div>
                      <div className="info-item">
                        <label>Lead Coach</label>
                        <p>{selectedClass.coach_name || 'Not assigned'}</p>
                      </div>
                      <div className="info-item">
                        <label>Assistant Coach</label>
                        <p>{selectedClass.assistant_coach_name || 'Not assigned'}</p>
                      </div>
                    </div>
                    {selectedClass.description && (
                      <div className="description">
                        <label>Description</label>
                        <p>{selectedClass.description}</p>
                      </div>
                    )}
                    {selectedClass.skill_requirements && (
                      <div className="requirements">
                        <label>Skill Requirements</label>
                        <p>{selectedClass.skill_requirements}</p>
                      </div>
                    )}
                    <div className="info-banner" style={{ 
                      background: '#dbeafe', 
                      border: '1px solid #3b82f6', 
                      borderRadius: '8px', 
                      padding: '12px 16px', 
                      marginTop: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>🗓</span>
                      <div>
                        <strong style={{ color: '#1e40af', display: 'block', marginBottom: '4px' }}>Class Scheduling</strong>
                        <span style={{ color: '#1e3a8a', fontSize: '0.875rem' }}>
                          To manage class times and schedules, use the <a href="/calendar" style={{ color: '#2563eb', fontWeight: 600 }}>Schedule</a> page.
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'roster' && (
                  <div className="roster">
                    {roster.length === 0 ? (
                      <p className="empty-state">No athletes enrolled yet</p>
                    ) : (
                      <>
                      <div className="roster-billing-bar">
                        <div className="rbb-summary">
                          <strong>{roster.length}</strong> enrolled ·
                          Monthly tuition total:{' '}
                          <strong>${roster.reduce((s, r) => s + netOf(r.tuition_amount, r.discount_type, r.discount_value), 0).toFixed(2)}</strong>
                          <span className="rbb-note"> (after discounts · billed automatically / from the Billing page)</span>
                        </div>
                      </div>
                      <table>
                        <thead>
                          <tr>
                            <th>Athlete Name</th>
                            <th>Age</th>
                            <th>Enrollment Date</th>
                            <th>Tuition</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {roster.map((enrollment) => (
                            <tr key={enrollment.id}>
                              <td>{enrollment.athlete_name}</td>
                              <td>{enrollment.age}</td>
                              <td>{new Date(enrollment.enrollment_date).toLocaleDateString()}</td>
                              <td>
                                <div>${netOf(enrollment.tuition_amount, enrollment.discount_type, enrollment.discount_value).toFixed(2)}/mo</div>
                                {enrollment.discount_type && enrollment.discount_type !== 'none' && Number(enrollment.discount_value) > 0 && (
                                  <div className="roster-disc">
                                    <s>${Number(enrollment.tuition_amount || 0).toFixed(2)}</s> · {enrollment.discount_reason || 'Discount'}
                                  </div>
                                )}
                                {enrollment.billing_option && <div className="roster-opt">{enrollment.billing_option}</div>}
                              </td>
                              <td>
                                {isAdmin && (
                                  <button
                                    className="btn-small-secondary"
                                    onClick={() => setEditEnrollment({
                                      id: enrollment.id,
                                      athlete_name: enrollment.athlete_name,
                                      tuition_amount: enrollment.tuition_amount ?? '',
                                      discount_type: enrollment.discount_type || 'none',
                                      discount_value: enrollment.discount_value || '',
                                      discount_reason: enrollment.discount_reason || ''
                                    })}
                                  >
                                    Edit
                                  </button>
                                )}
                                <button
                                  className="btn-danger-small"
                                  onClick={() => handleDropAthlete(enrollment.id)}
                                >
                                  Drop
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </>
                    )}
                  </div>
                )}

                {activeTab === 'waitlist' && (
                  <div className="waitlist">
                    {waitlist.length === 0 ? (
                      <p className="empty-state">No one on waitlist</p>
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Position</th>
                            <th>Athlete Name</th>
                            <th>Age</th>
                            <th>Added Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {waitlist.map((item) => (
                            <tr key={item.id}>
                              <td>#{item.position}</td>
                              <td>{item.athlete_name}</td>
                              <td>{item.age}</td>
                              <td>{new Date(item.added_at).toLocaleDateString()}</td>
                              <td>
                                <button
                                  className="btn-promote"
                                  onClick={() => handlePromoteFromWaitlist(item)}
                                >
                                  Promote ✓
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {activeTab === 'schedule' && (
                  <div className="class-schedule">
                    <div className="schedule-header">
                      <h3>Regular Practice Times</h3>
                      <button className="btn-primary" onClick={() => {
                        setEditingScheduleId(null);
                        setScheduleForm({ day_of_week: 'Monday', start_time: '17:00', end_time: '19:00', start_date: '', end_date: '' });
                        setShowAddSchedule(true);
                      }}>
                        Add Practice Time
                      </button>
                    </div>

                    {classSchedule.length === 0 ? (
                      <div className="empty-state">
                        <p>No recurring practice schedule set</p>
                        <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                          Add practice times to automatically track expected attendance
                        </p>
                      </div>
                    ) : (
                      <div className="schedule-grid">
                        {classSchedule.map((schedule) => (
                          <div key={schedule.id} className="schedule-card">
                            <div className="schedule-day">{schedule.day_of_week}</div>
                            <div className="schedule-time">
                              {schedule.start_time.substring(0, 5)} - {schedule.end_time.substring(0, 5)}
                            </div>
                            {(schedule.start_date || schedule.end_date) && (
                              <div className="schedule-dates" style={{ fontSize: '0.8rem', color: '#666' }}>
                                {(schedule.start_date ? String(schedule.start_date).slice(0, 10) : 'always')}
                                {' → '}
                                {(schedule.end_date ? String(schedule.end_date).slice(0, 10) : 'ongoing')}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                              <button
                                className="btn-secondary"
                                onClick={() => openEditSchedule(schedule)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn-delete"
                                onClick={() => handleDeleteSchedule(schedule.id)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {showAddSchedule && (
                      <div className="modal-overlay" onClick={() => { setShowAddSchedule(false); setEditingScheduleId(null); }}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                          <h2>{editingScheduleId ? 'Edit Practice Time' : 'Add Practice Time'}</h2>
                          <form onSubmit={handleAddSchedule}>
                            <div className="form-group">
                              <label>Day of Week</label>
                              <select
                                value={scheduleForm.day_of_week}
                                onChange={(e) => setScheduleForm({...scheduleForm, day_of_week: e.target.value})}
                                required
                              >
                                <option value="Monday">Monday</option>
                                <option value="Tuesday">Tuesday</option>
                                <option value="Wednesday">Wednesday</option>
                                <option value="Thursday">Thursday</option>
                                <option value="Friday">Friday</option>
                                <option value="Saturday">Saturday</option>
                                <option value="Sunday">Sunday</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Start Time</label>
                              <input
                                type="time"
                                value={scheduleForm.start_time}
                                onChange={(e) => setScheduleForm({...scheduleForm, start_time: e.target.value})}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>End Time</label>
                              <input
                                type="time"
                                value={scheduleForm.end_time}
                                onChange={(e) => setScheduleForm({...scheduleForm, end_time: e.target.value})}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Season start (optional)</label>
                              <input
                                type="date"
                                value={scheduleForm.start_date}
                                onChange={(e) => setScheduleForm({...scheduleForm, start_date: e.target.value})}
                              />
                            </div>
                            <div className="form-group">
                              <label>Season end (optional)</label>
                              <input
                                type="date"
                                value={scheduleForm.end_date}
                                onChange={(e) => setScheduleForm({...scheduleForm, end_date: e.target.value})}
                              />
                              <p style={{ color: '#666', fontSize: '0.8rem', marginTop: '4px' }}>
                                Leave dates blank for an ongoing weekly practice. Set a range
                                to schedule a season (e.g. Summer vs Fall) — you can add a
                                second practice on the same day for the other season.
                              </p>
                            </div>
                            <div className="modal-buttons">
                              <button type="button" className="btn-secondary" onClick={() => { setShowAddSchedule(false); setEditingScheduleId(null); }}>
                                Cancel
                              </button>
                              <button type="submit" className="btn-primary">
                                {editingScheduleId ? 'Save Changes' : 'Add Practice Time'}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </>
          ) : (
            <div className="empty-state-large">
              <p>Select a class to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit Class Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => { setShowCreateModal(false); setEditingClassId(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingClassId ? 'Edit Class' : 'Create New Class'}</h2>
            <form onSubmit={handleCreateClass}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Class Name*</label>
                  <input
                    type="text"
                    required
                    value={formData.class_name}
                    onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Level*</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., Level 3, Xcel Silver"
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Program Type</label>
                  <select
                    value={formData.program_type}
                    onChange={(e) => setFormData({ ...formData, program_type: e.target.value })}
                  >
                    <option value="competitive">Competitive</option>
                    <option value="recreational">Recreational</option>
                    <option value="preschool">Preschool</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Age Range</label>
                  <input
                    type="text"
                    placeholder="e.g., 6-8 years"
                    value={formData.age_range}
                    onChange={(e) => setFormData({ ...formData, age_range: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Max Capacity</label>
                  <input
                    type="number"
                    value={formData.max_capacity}
                    onChange={(e) => setFormData({ ...formData, max_capacity: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Monthly Tuition ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monthly_tuition}
                    onChange={(e) => setFormData({ ...formData, monthly_tuition: e.target.value })}
                  />
                  <small>Default price. Add more options below (per-week tiers, drop-in…) if you want choices at enrollment.</small>
                </div>

                <div className="form-group full-width pricing-options-editor">
                  <label>Billing Options (optional)</label>
                  {formData.pricing_options.map((opt, i) => (
                    <div key={i} className="pricing-option-row">
                      <input
                        type="text"
                        placeholder="Label (e.g., 2x / week)"
                        value={opt.label}
                        onChange={(e) => updateOpt(i, 'label', e.target.value)}
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Amount"
                        value={opt.amount}
                        onChange={(e) => updateOpt(i, 'amount', e.target.value)}
                      />
                      <select value={opt.cadence} onChange={(e) => updateOpt(i, 'cadence', e.target.value)}>
                        <option value="monthly">/month</option>
                        <option value="per_session">per class</option>
                      </select>
                      <button type="button" className="opt-remove" onClick={() => removeOpt(i)}>×</button>
                    </div>
                  ))}
                  <button type="button" className="btn-add-option" onClick={addOpt}>+ Add billing option</button>
                  <small>e.g. “1x / week $80/mo”, “2x / week $120/mo”, “Drop-in $20 per class”. Per-class options are billed ad-hoc.</small>
                </div>

                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowCreateModal(false); setEditingClassId(null); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingClassId ? 'Save Changes' : 'Create Class'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enroll Athlete Modal */}
      {showEnrollModal && selectedClass && (
        <div className="modal-overlay" onClick={() => setShowEnrollModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Enroll Athletes in {selectedClass.class_name}</h2>
            <form onSubmit={handleEnrollAthlete}>
              <div className="form-group">
                <label>
                  Select Athletes*
                  {selectedAthleteIds.length > 0 && ` (${selectedAthleteIds.length} selected)`}
                </label>
                {(() => {
                  const available = athletes.filter(
                    (a) => !roster.some((e) => e.athlete_id === a.id)
                  );
                  if (available.length === 0) {
                    return (
                      <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                        All team athletes are already enrolled in this class.
                      </p>
                    );
                  }
                  const allSelected = available.every((a) => selectedAthleteIds.includes(a.id));
                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.25rem' }}>
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedAthleteIds(allSelected ? [] : available.map((a) => a.id))
                          }
                          style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '0.85rem' }}
                        >
                          {allSelected ? 'Clear all' : 'Select all'}
                        </button>
                      </div>
                      <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '6px' }}>
                        {available.map((a) => {
                          // Show the athlete's level when available (normalise "7" -> "Level 7").
                          const lvl = a.level
                            ? (String(a.level).match(/^level/i) ? a.level : `Level ${a.level}`)
                            : null;
                          return (
                          <label
                            key={a.id}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedAthleteIds.includes(a.id)}
                              onChange={() =>
                                setSelectedAthleteIds((ids) =>
                                  ids.includes(a.id) ? ids.filter((x) => x !== a.id) : [...ids, a.id]
                                )
                              }
                              // Override .form-group input { width:100% } which otherwise
                              // stretches the checkbox across the row and hides the name.
                              style={{ width: '16px', height: '16px', flexShrink: 0, margin: 0, padding: 0 }}
                            />
                            <span style={{ color: '#333' }}>{a.name}{lvl ? ` (${lvl})` : ''}</span>
                          </label>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
              {parseOptions(selectedClass).length > 0 && (
                <div className="form-group">
                  <label>Billing Option</label>
                  <select
                    value={enrollData.billing_option}
                    onChange={(e) => {
                      const opt = parseOptions(selectedClass).find((o) => o.label === e.target.value);
                      setEnrollData({ ...enrollData, billing_option: e.target.value, tuition_amount: opt ? opt.amount : enrollData.tuition_amount });
                    }}
                  >
                    <option value="">— choose a pricing option —</option>
                    {parseOptions(selectedClass).map((o, i) => (
                      <option key={i} value={o.label}>{o.label} — ${o.amount}{o.cadence === 'per_session' ? ' /class' : '/mo'}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Tuition ($) {enrollData.billing_option ? '— override' : ''}</label>
                <input
                  type="number"
                  step="0.01"
                  value={enrollData.tuition_amount || selectedClass.monthly_tuition}
                  onChange={(e) => setEnrollData({ ...enrollData, tuition_amount: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Payment Frequency</label>
                <select
                  value={enrollData.payment_frequency}
                  onChange={(e) => setEnrollData({ ...enrollData, payment_frequency: e.target.value })}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div className="form-group">
                <label>Discount (optional — applies to selected athletes)</label>
                <div className="discount-row">
                  <select
                    value={enrollData.discount_type}
                    onChange={(e) => setEnrollData({ ...enrollData, discount_type: e.target.value })}
                  >
                    <option value="none">No discount</option>
                    <option value="percent">Percent %</option>
                    <option value="fixed">Fixed $</option>
                  </select>
                  {enrollData.discount_type !== 'none' && (
                    <>
                      <input
                        type="number" step="0.01"
                        placeholder={enrollData.discount_type === 'percent' ? '%' : '$'}
                        value={enrollData.discount_value}
                        onChange={(e) => setEnrollData({ ...enrollData, discount_value: e.target.value })}
                      />
                      <input
                        type="text" placeholder="Reason (e.g., Sibling)"
                        value={enrollData.discount_reason}
                        onChange={(e) => setEnrollData({ ...enrollData, discount_reason: e.target.value })}
                      />
                    </>
                  )}
                </div>
                {enrollData.discount_type !== 'none' && (enrollData.tuition_amount || selectedClass.monthly_tuition) && (
                  <small>Net after discount: <strong>${netOf(enrollData.tuition_amount || selectedClass.monthly_tuition, enrollData.discount_type, enrollData.discount_value)}</strong>/mo each</small>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEnrollModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={selectedAthleteIds.length === 0}>
                  Enroll{selectedAthleteIds.length > 0 ? ` (${selectedAthleteIds.length})` : ''}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Per-athlete billing edit (custom tuition / sibling discount) */}
      {editEnrollment && (
        <div className="modal-overlay" onClick={() => setEditEnrollment(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Billing — {editEnrollment.athlete_name}</h2>
            <div className="form-group">
              <label>Tuition ($/mo)</label>
              <input
                type="number" step="0.01"
                value={editEnrollment.tuition_amount}
                onChange={(e) => setEditEnrollment({ ...editEnrollment, tuition_amount: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Discount</label>
              <div className="discount-row">
                <select
                  value={editEnrollment.discount_type}
                  onChange={(e) => setEditEnrollment({ ...editEnrollment, discount_type: e.target.value })}
                >
                  <option value="none">No discount</option>
                  <option value="percent">Percent %</option>
                  <option value="fixed">Fixed $</option>
                </select>
                {editEnrollment.discount_type !== 'none' && (
                  <>
                    <input
                      type="number" step="0.01"
                      placeholder={editEnrollment.discount_type === 'percent' ? '%' : '$'}
                      value={editEnrollment.discount_value}
                      onChange={(e) => setEditEnrollment({ ...editEnrollment, discount_value: e.target.value })}
                    />
                    <input
                      type="text" placeholder="Reason (e.g., Sibling)"
                      value={editEnrollment.discount_reason}
                      onChange={(e) => setEditEnrollment({ ...editEnrollment, discount_reason: e.target.value })}
                    />
                  </>
                )}
              </div>
              {editEnrollment.discount_type !== 'none' && (
                <small>Net: <strong>${netOf(editEnrollment.tuition_amount, editEnrollment.discount_type, editEnrollment.discount_value).toFixed(2)}</strong>/mo</small>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setEditEnrollment(null)}>Cancel</button>
              <button type="button" className="btn-primary" onClick={handleSaveEnrollmentEdit}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassManagement;
